import { CardTitle } from "@/components/ui/card";
import type {
  NumberRequestDocument,
  NumberRequestKind,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { CheckCircleIcon } from "@phosphor-icons/react";
import { DocumentItem } from "./document-item";

/**
 * The checklist, shared by both paperwork kinds.
 *
 * The two lists overlap almost entirely — the same component renders both, and
 * only the per-document copy changes (see `numberRequests.documents.<kind>` in
 * the locales). What differs is the count and the order, which come from the
 * request itself, so this stays honest even if the backend's list changes.
 */

/**
 * Asked for in the order they are *found*, not the order the backend stores
 * them in: the ID is in the client's pocket, the bill is on their desk, and
 * the Kbis means a trip to infogreffe — so that is the order. A type absent
 * from this list keeps its backend position, at the end.
 *
 * The two number-producing kinds ask for the same three: keeping your number
 * no longer means porting it, so there is no line to prove ownership of.
 */
const DOCUMENT_ORDER: Record<NumberRequestKind, string[]> = {
  new_number: ["proof_of_identity", "proof_of_address", "company_registration"],
  port_existing: [
    "proof_of_identity",
    "proof_of_address",
    "company_registration",
  ],
  sip_trunk: [],
};

const orderDocuments = (
  kind: NumberRequestKind,
  documents: NumberRequestDocument[],
) => {
  const order = DOCUMENT_ORDER[kind] || [];
  const rank = (document: NumberRequestDocument) => {
    const index = order.indexOf(document.type);
    return index < 0 ? order.length : index;
  };
  return [...documents].sort((a, b) => rank(a) - rank(b));
};

export function DocumentChecklist({
  clientId,
  requestId,
  kind,
  documents,
  address,
  country,
  intro,
}: {
  clientId: string;
  requestId: string;
  kind: NumberRequestKind;
  documents: NumberRequestDocument[];
  address?: string | null;
  country?: string | null;
  /** In place of the kind's own intro, which counts its three papers. */
  intro?: string;
}) {
  const { t } = useTranslation("numberRequests");

  const provided = documents.filter((document) => document.provided).length;
  const complete = documents.length > 0 && provided === documents.length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <CardTitle>{t("numberRequests.documents.title")}</CardTitle>
          <p className="text-muted-foreground text-sm">
            {intro ?? t(`numberRequests.documents.${kind}.intro`)}
          </p>
        </div>
        <span className="text-muted-foreground shrink-0 text-sm tabular-nums">
          {t("numberRequests.stepCount", {
            current: provided,
            total: documents.length,
          })}
        </span>
      </div>

      {orderDocuments(kind, documents).map((document) => (
        <DocumentItem
          key={document.type}
          clientId={clientId}
          requestId={requestId}
          kind={kind}
          document={document}
          address={address}
          country={country}
        />
      ))}

      {complete ? (
        <p className="text-primary flex items-center gap-2 text-sm">
          <CheckCircleIcon className="h-4 w-4" weight="fill" />
          {t("numberRequests.documents.allProvided")}
        </p>
      ) : (
        <p className="text-muted-foreground text-sm">
          {t("numberRequests.documents.formats")}
        </p>
      )}
    </div>
  );
}
